import { cn } from '@shared/lib/cn'
import type { ComponentType, ReactNode } from 'react'
import { HiMiniChevronRight } from 'react-icons/hi2'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './accordion'

interface MenuRowProps {
  readonly icon: ComponentType<{ className?: string }>
  readonly label: string
  readonly onClick: () => void
}

export function MenuRow({ icon: Icon, label, onClick }: MenuRowProps) {
  return (
    <button
      type="button"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-3 transition-colors hover:bg-muted/20 active:bg-muted/20"
      onClick={onClick}
    >
      <span className="flex items-center gap-1.5">
        <Icon className="text-xl text-muted-foreground" />
        <span className="text-body font-medium text-foreground">{label}</span>
      </span>
      <HiMiniChevronRight className="text-xl text-muted-foreground" />
    </button>
  )
}

interface MenuRowAccordionProps {
  readonly icon: ComponentType<{ className?: string }>
  readonly label: string
  readonly count?: number
  readonly defaultOpen?: boolean
  readonly children: ReactNode
}

function MenuRowAccordion({
  icon: Icon,
  label,
  count,
  defaultOpen = false,
  children,
}: MenuRowAccordionProps) {
  return (
    <Accordion defaultValue={defaultOpen ? ['item'] : []}>
      <AccordionItem value="item" className="border-b-0">
        <AccordionTrigger
          className={cn(
            'cursor-pointer rounded-lg px-3 py-3 transition-colors hover:bg-muted/20 active:bg-muted/20',
          )}
        >
          <span className="flex w-full justify-between items-center gap-1">
            <div className="flex items-center gap-1.5">
              <Icon className="text-xl text-muted-foreground" />
              <span className="text-body font-medium text-foreground">{label}</span>
            </div>
            {typeof count === 'number' && (
              <span className="typo-footnote border px-1 rounded-[4px] border-border inline-block font-semibold text-primary/70">
                <strong>{count}</strong>개
              </span>
            )}
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-4">{children}</AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

interface MenuRowActionProps {
  readonly label: string
  readonly onClick: () => void
}

function MenuRowAction({ label, onClick }: MenuRowActionProps) {
  return (
    <button
      type="button"
      className="flex w-full cursor-pointer items-center rounded-lg px-3 py-3 transition-colors hover:bg-muted/20 active:bg-muted/20"
      onClick={onClick}
    >
      <span className="text-body font-medium text-foreground">{label}</span>
    </button>
  )
}

MenuRow.Action = MenuRowAction
MenuRow.Accordion = MenuRowAccordion
